import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, EntityService, SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AddFavProductPayload, buildFavProductKey, FavProductKey, PmFavProduct, RemoveFavProductPayload } from '../../models/pm-fav-product.model';
import { BulkPrice, CartUpdateEvent, MarketBestPrice, SearchItem, SupplierProduct, SupplierSection } from '../../models/supplier.models';
import { DirectPriceDiscoveryService, DirectSearchResponse } from '../../services/direct-price-discovery.service';
import { PriceDiscoveryService } from '../../services/price-discovery.service';
import { PurchaseOrderService } from '../../services/purchase-order.service';
import { SupplierRegistryService } from '../../services/supplier-registry.service';
import { DmartSupplierComponent } from '../dmart-supplier/dmart-supplier.component';
import { HyperpureSupplierComponent } from '../hyperpure-supplier/hyperpure-supplier.component';
import { LocalSupplierPurchaseOrderModalComponent } from '../local-supplier-create-po/local-supplier-purchase-order-modal.component';
import { UdaanSupplierComponent } from '../udaan-supplier/udaan-supplier.component';

/**
 * Best match candidate for the popup
 */
interface BestMatchCandidate {
    searchItemName: string;
    platform: string;
    platformLabel: string;
    item: SupplierProduct;
    isSelected: boolean;
    isAlreadyAdded: boolean;
    isAlreadyInCart: boolean;
    existingCartQty: number;
    minimumOrderQty: number;
    quantity: number;
}

/**
 * Price Discovery Direct Component
 *
 * This component is a copy of PmPriceDiscoveryComponent but uses direct
 * supplier search API that bypasses the GCP layer for faster response times.
 *
 * Key differences from the original:
 * - Uses DirectPriceDiscoveryService instead of PriceDiscoveryService for search
 * - No polling mechanism - results are returned immediately
 * - Best match calculations are done server-side
 *
 * Route: /purchase-manager/price-discovery-direct
 */
@Component({
    selector: 'app-pm-price-discovery-direct',
    templateUrl: './pm-price-discovery-direct.component.html',
    styleUrls: ['./pm-price-discovery-direct.component.scss']
})
export class PmPriceDiscoveryDirectComponent implements OnInit, OnDestroy {

    // Entity
    selectedEntityId = '';

    // Search Items (left sidebar)
    searchItems: SearchItem[] = [];
    activeSearchItem: string = '';
    newItemSearchText: string = '';

    // Quick Add categories
    quickAddCategories: { label: string; icon: string; key: string; active: boolean; addedItems: string[] }[] = [
        { label: 'Recent Purchased', icon: 'schedule', key: 'recentPurchase', active: false, addedItems: [] },
        { label: 'Most Purchased', icon: 'shopping_cart', key: 'mostPurchased', active: false, addedItems: [] },
        { label: 'Last Searched', icon: 'history', key: 'lastSearched', active: false, addedItems: [] },
        { label: 'Most Searched', icon: 'search', key: 'mostSearched', active: false, addedItems: [] },
        { label: 'Top 10 Ordered', icon: 'trending_up', key: 'topOrdered', active: false, addedItems: [] }
    ];
    isQuickAddLoading = false;

    // Supplier sections
    supplierSections: SupplierSection[] = [];

    // Selected card tracking
    selectedCard: SupplierProduct | null = null;
    allSuppliers: any[] = [];

    // Market best price (top banner)
    marketBestPrice: MarketBestPrice | null = null;

    // Loading states
    isLoading = false;
    isInitializing = true;
    private isInitialLoad = true;

    /**
     * Checks if the currently active search item is still loading its results.
     */
    get isActiveItemLoading(): boolean {
        if (!this.activeSearchItem) return this.isInitializing;
        const activeItem = this.searchItems.find(item => item.name === this.activeSearchItem);
        return activeItem ? activeItem.isLoading || false : false;
    }

    // Drawer state
    isDrawerOpen = false;
    selectedSection: SupplierSection | null = null;
    drawerSearchText = '';
    drawerSortOption = 'relevance';

    // Checkout Drawer state
    isCheckoutDrawerOpen = false;
    selectedCheckoutCart: any = null;

    /**
     * Returns filtered and sorted items for the drawer.
     */
    get drawerFilteredItems(): SupplierProduct[] {
        if (!this.selectedSection) return [];

        let items = this.getItemsForActiveSearch(this.selectedSection);

        if (this.drawerSearchText) {
            const query = this.drawerSearchText.toLowerCase();
            items = items.filter(item =>
                item.item_name.toLowerCase().includes(query) ||
                (item.sku_id && item.sku_id.toLowerCase().includes(query))
            );
        }

        return items.sort((a, b) => {
            if (this.drawerSortOption === 'price-low') return a.rate - b.rate;
            if (this.drawerSortOption === 'price-high') return b.rate - a.rate;
            if (a.isBestMatch && !b.isBestMatch) return -1;
            if (!a.isBestMatch && b.isBestMatch) return 1;
            if (a.is_in_stock && !b.is_in_stock) return -1;
            if (!a.is_in_stock && b.is_in_stock) return 1;
            return 0;
        });
    }

    // Integration status
    isHyperpureEnabled = false;
    isDmartEnabled = false;
    isUdaanEnabled = false;
    enableLocalSupplier = false;
    hyperpureStoreUuid = '';
    hyperpureSessionId = '';
    hyperpureLoginData: any = null;
    udaanStoreUuid = '';
    dmartStoreId = '';

    // Child component references
    @ViewChild('hyperpureSupplier') hyperpureSupplier!: HyperpureSupplierComponent;
    @ViewChild('dmartSupplier') dmartSupplier!: DmartSupplierComponent;
    @ViewChild('udaanSupplier') udaanSupplier!: UdaanSupplierComponent;
    @ViewChild('localSupplierPOModal') localSupplierPOModal!: LocalSupplierPurchaseOrderModalComponent;

    // Suppliers map for resolving names
    suppliersMap: Map<string, string> = new Map();

    // Favourite products state
    favProductKeys: Set<FavProductKey> = new Set();
    favProductsList: PmFavProduct[] = [];
    isLoadingFavProducts = false;
    savingFavProduct: Set<FavProductKey> = new Set();

    // Sidebar accordion state
    activeSidebarSection: 'recent' | 'favourites' = 'recent';
    favSidebarSearchTerm = '';

    // Fav view mode
    isFavViewMode = false;
    isLoadingAllFav = false;
    activeFavItem: string = '';

    // Dynamic card display
    maxVisibleCards = 4;

    // Mobile responsive
    isMobileView = false;
    isSidebarOpen = false;

    // Cart and checkout state
    isSessionItemsCollapsed = false;
    isDmartSessionItemsCollapsed = false;
    isCartItemsCollapsed = true;
    isDmartCartItemsCollapsed = true;

    // Market Best / Best Match state
    isMarketBestItemsCollapsed = false;
    bestMatchCandidates: BestMatchCandidate[] = [];
    marketBestItemSkus: Set<string> = new Set();
    marketBestItemsSnapshot: { platform: string; skuId: string; previousQty: number }[] = [];
    hasMarketBestItems = false;
    isAddingBestMatches = false;
    isBestMatchPopupOpen = false;
    selectAllBestMatches = false;

    // Cart highlight effect
    highlightedCartPlatform: string | null = null;
    cartHighlightAction: 'add' | 'remove' | null = null;
    private highlightTimeout: any = null;

    private destroy$ = new Subject<void>();

    /**
     * Returns session-modified items for the current checkout cart.
     */
    get sessionModifiedItems(): SupplierProduct[] {
        if (!this.selectedCheckoutCart) return [];
        const items = this.selectedCheckoutCart.items || [];
        if (this.selectedCheckoutCart.platform === 'hyperpure' && this.hyperpureSupplier) {
            return this.hyperpureSupplier.getSessionModifiedItems(items);
        }
        if (this.selectedCheckoutCart.platform === 'dmart' && this.dmartSupplier) {
            return this.dmartSupplier.getSessionModifiedItems(items);
        }
        return [];
    }

    /**
     * Returns non-session cart items.
     */
    get nonSessionCartItems(): SupplierProduct[] {
        if (!this.selectedCheckoutCart) return [];
        const items = this.selectedCheckoutCart.items || [];
        if (this.selectedCheckoutCart.platform === 'hyperpure' && this.hyperpureSupplier) {
            return this.hyperpureSupplier.getNonSessionCartItems(items);
        }
        if (this.selectedCheckoutCart.platform === 'dmart' && this.dmartSupplier) {
            return this.dmartSupplier.getNonSessionCartItems(items);
        }
        return items;
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private directPriceDiscoveryService: DirectPriceDiscoveryService,
        private priceDiscoveryService: PriceDiscoveryService, // For cart operations
        private entityService: EntityService,
        private authService: AuthService,
        private sharedService: SharedService,
        private supplierRegistry: SupplierRegistryService,
        private purchaseOrderService: PurchaseOrderService
    ) { }

    ngOnInit(): void {
        this.calculateMaxVisibleCards();
        window.addEventListener('resize', () => this.calculateMaxVisibleCards());

        this.checkMobileView();
        window.addEventListener('resize', () => this.checkMobileView());

        const selectedEntity = this.entityService.getSingleSelectedEntity();
        if (selectedEntity) {
            this.selectedEntityId = selectedEntity.id;
        }

        this.loadMarketBestItems();

        this.entityService.singleSelectedEntity$
            .pipe(takeUntil(this.destroy$))
            .subscribe(entity => {
                if (entity && entity.id && this.selectedEntityId && entity.id !== this.selectedEntityId) {
                    this.resetForEntityChange(entity.id);
                }
            });

        this.loadSuppliers();
        this.loadFavouriteProducts();

        this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const tagsParam = this.sharedService.decryptFromUrl(params['tags']);

            // Only process full initialization on initial page load
            // Skip if this is an in-session URL update (e.g., when adding a new item)
            if (!this.isInitialLoad) {
                return;
            }

            if (tagsParam) {
                const tags = tagsParam.split('$$,$$').map((t: string) => t.trim()).filter((t: string) => t.length > 0);
                this.searchItems = tags.map((name: string) => ({
                    name,
                    isLoading: true
                }));
                if (this.searchItems.length > 0) {
                    this.activeSearchItem = this.searchItems[0].name;
                }

                const savedActiveKey = localStorage.getItem(`pm_activeQuickAdd_${this.selectedEntityId}`);
                if (savedActiveKey) {
                    const savedCat = this.quickAddCategories.find(c => c.key === savedActiveKey);
                    if (savedCat) { savedCat.active = true; }
                }
            }

            if (this.selectedEntityId) {
                this.isInitialLoad = false;
                this.initializeAndSearch();

                if (!tagsParam) {
                    const recentCat = this.quickAddCategories.find(c => c.key === 'recentPurchase');
                    if (recentCat) {
                        setTimeout(() => this.onQuickAddClick(recentCat), 1500);
                    }
                }
            } else {
                this.sharedService.errorAlert('No entity selected');
                this.isInitializing = false;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        document.body.style.overflow = '';
        window.removeEventListener('resize', () => this.calculateMaxVisibleCards());
        window.removeEventListener('resize', () => this.checkMobileView());
    }

    /**
     * Resets all component state when the entity changes.
     */
    private resetForEntityChange(newEntityId: string): void {
        localStorage.removeItem('hyperpureCart');
        localStorage.removeItem('dmartCart');
        localStorage.removeItem('hyperpureSessionItems');
        localStorage.removeItem('dmartSessionItems');
        this.clearMarketBestItems();

        this.searchItems = [];
        this.activeSearchItem = '';
        this.newItemSearchText = '';
        this.supplierSections = [];
        this.marketBestPrice = null;
        this.selectedCard = null;
        this.allSuppliers = [];
        this.suppliersMap = new Map();
        this.favProductKeys = new Set();
        this.favProductsList = [];
        this.savingFavProduct = new Set();
        this.isLoading = false;
        this.isInitializing = true;
        this.isDrawerOpen = false;
        this.selectedSection = null;
        this.drawerSearchText = '';
        this.drawerSortOption = 'relevance';
        this.isCheckoutDrawerOpen = false;
        this.selectedCheckoutCart = null;
        this.isHyperpureEnabled = false;
        this.hyperpureStoreUuid = '';
        this.hyperpureSessionId = '';
        this.hyperpureLoginData = null;
        this.isDmartEnabled = false;
        this.dmartStoreId = '';
        this.isUdaanEnabled = false;
        this.udaanStoreUuid = '';
        this.enableLocalSupplier = false;

        if (this.hyperpureSupplier) this.hyperpureSupplier.resetState();
        if (this.dmartSupplier) this.dmartSupplier.resetState();
        if (this.udaanSupplier) this.udaanSupplier.resetState();

        this.selectedEntityId = newEntityId;
        this.loadSuppliers();
        this.initializeAndSearch();
    }

    /**
     * Loads suppliers for name mapping.
     */
    private loadSuppliers(): void {
        if (!this.selectedEntityId) return;

        this.directPriceDiscoveryService.getSuppliers(this.selectedEntityId, true)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (suppliers: any[]) => {
                    this.allSuppliers = suppliers || [];
                    (suppliers || []).forEach(s => {
                        const id = s.id || s._id;
                        if (id) {
                            this.suppliersMap.set(id, s.name || s.alias || s.supplierAlias || 'Unknown Supplier');
                        }
                    });
                },
                (error: any) => {
                    console.warn('Failed to load supplier names for mapping', error);
                }
            );
    }

    /**
     * Resolves a supplier ID to its human-readable name.
     */
    resolveSupplierName(supplierRef: string | undefined): string {
        if (!supplierRef) return '';
        return this.suppliersMap.get(supplierRef) || supplierRef;
    }

    /**
     * Checks integration status and initializes supplier sections.
     */
    private initializeAndSearch(): void {
        this.isInitializing = true;

        this.directPriceDiscoveryService.checkIntegrationStatus(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.isHyperpureEnabled = response?.isHyperpureEnabled || false;
                    this.isDmartEnabled = response?.isDmartEnabled || false;
                    this.isUdaanEnabled = response?.isUdaanEnabled || false;
                    this.enableLocalSupplier = response?.enableLocalSupplier || false;

                    if (this.isHyperpureEnabled) {
                        this.hyperpureStoreUuid = response?.hyperpureStoreUuid || '';
                        this.hyperpureLoginData = {
                            storeId: this.hyperpureStoreUuid,
                            email: response?.hyperpureEmail,
                            phone: response?.hyperpurePhone,
                            storeName: response?.hyperpureStoreData?.storeName,
                            storeGst: response?.hyperpureStoreData?.storeGst,
                            storeAddress: response?.hyperpureStoreData?.storeAddress,
                            storePincode: response?.hyperpureStoreData?.storePincode
                        };
                    }

                    if (this.isUdaanEnabled) {
                        this.udaanStoreUuid = response?.udaanStoreData?.storeId || response?.udaanId || '';
                    }

                    if (this.isDmartEnabled) {
                        this.dmartStoreId = response?.dmartStoreData?.storeId || response?.dmartId || '';
                    }

                    this.initSupplierSections();

                    if (this.isHyperpureEnabled && this.hyperpureStoreUuid) {
                        this.fetchHyperpureLogin().then(() => {
                            this.isInitializing = false;
                            this.startDirectSearches();
                        });
                    } else {
                        this.isInitializing = false;
                        this.startDirectSearches();
                    }

                    if (this.isDmartEnabled && this.dmartStoreId) {
                        setTimeout(() => {
                            if (this.dmartSupplier) {
                                const dmartSection = this.supplierSections.find(s => s.id === 'dmart');
                                this.dmartSupplier.entityId = this.selectedEntityId;
                                this.dmartSupplier.storeId = this.dmartStoreId;
                                this.dmartSupplier.supplierSection = dmartSection || null;
                                this.dmartSupplier.initialize();
                            }
                        });
                    }
                },
                (error: any) => {
                    console.error('Failed to check integration status', error);
                    this.isInitializing = false;
                    this.initSupplierSections();
                    this.startDirectSearches();
                }
            );
    }

    /**
     * Initializes supplier sections based on enabled integrations.
     */
    private initSupplierSections(): void {
        this.supplierSections = [];

        const standardSections = [
            { id: 'hyperpure', name: 'Hyperpure', logo: 'hyperpure', color: '#fad2d1' },
            { id: 'dmart', name: 'DMart', logo: 'dmart', color: '#d4edda' },
            { id: 'udaan', name: 'Udaan', logo: 'udaan', color: '#cce5ff' },
            { id: 'local', name: 'Local Suppliers', logo: 'local', color: '#fff3cd', isLocalSupplier: true }
        ];

        standardSections.forEach(config => {
            const isEnabled = (config.id === 'hyperpure' && this.isHyperpureEnabled) ||
                (config.id === 'udaan' && this.isUdaanEnabled) ||
                (config.id === 'dmart' && this.isDmartEnabled) ||
                (config.id === 'local' && this.enableLocalSupplier) ||
                this.isStandardSection(config.id);

            if (isEnabled) {
                this.supplierSections.push({
                    id: config.id,
                    name: config.name,
                    logo: config.logo,
                    color: config.color,
                    items: [],
                    totalItems: 0,
                    isVisible: true,
                    isLocalSupplier: config.isLocalSupplier
                });
            }
        });
    }

    /**
     * Fetches Hyperpure login data.
     */
    private fetchHyperpureLogin(): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(() => {
                if (this.hyperpureSupplier) {
                    const hpSection = this.supplierSections.find(s => s.id === 'hyperpure');
                    this.hyperpureSupplier.entityId = this.selectedEntityId;
                    this.hyperpureSupplier.storeUuid = this.hyperpureStoreUuid;
                    this.hyperpureSupplier.supplierSection = hpSection || null;
                    this.hyperpureSupplier.initialize();
                }
            });
            resolve();
        });
    }

    /**
     * Starts direct searches for all search items.
     * This is the KEY DIFFERENCE from the original component.
     * Instead of polling, we make direct API calls.
     */
    private startDirectSearches(): void {
        if (this.searchItems.length === 0) return;

        this.isLoading = true;

        this.searchItems.forEach(searchItem => {
            this.directSearchItem(searchItem);
        });
    }

    /**
     * Performs a direct search for a single item.
     * This bypasses the GCP layer and returns results immediately.
     */
    private directSearchItem(searchItem: SearchItem): void {
        searchItem.isLoading = true;

        const payload = {
            entityId: this.selectedEntityId,
            itemName: searchItem.name.trim()
        };

        this.directPriceDiscoveryService.searchDirect(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    searchItem.isLoading = false;
                    // Use response.response if present, else fallback to response
                    const directResult = response && response.response ? response.response : response;
                    this.processDirectResults(directResult, searchItem);
                    this.checkAllSearchesComplete();
                },
                (error: any) => {
                    console.error('Direct search failed for', searchItem.name, error);
                    searchItem.isLoading = false;
                    this.checkAllSearchesComplete();
                }
            );
    }

    /**
     * Processes direct search results and organizes them into supplier sections.
     * The response includes pre-calculated best matches from the server.
     */
    private processDirectResults(response: DirectSearchResponse, searchItem: SearchItem): void {
        const supplierResults: { [key: string]: { bestMatch: any; similarItems: any[] } } = {};

        // Process each supplier's results
        ['hyperpure', 'dmart', 'udaan'].forEach(supplierId => {
            const data = (response as any)[supplierId];
            if (data && Object.keys(data).length > 0) {
                const mapProductImages = (item: any) => {
                    if (item && !item.image_url && !item.imageUrl) {
                        const imgUrl = item.productImages || item.product_images;
                        if (imgUrl) {
                            if (typeof imgUrl === 'string') {
                                item.image_url = imgUrl;
                                item.imageUrl = imgUrl;
                            } else if (Array.isArray(imgUrl) && imgUrl.length > 0) {
                                item.image_url = imgUrl[0];
                                item.imageUrl = imgUrl[0];
                            }
                        }
                    }
                };

                if (data.best_match || data.bestMatch) mapProductImages(data.best_match || data.bestMatch);
                if (Array.isArray(data.similar_items)) data.similar_items.forEach(mapProductImages);
                if (Array.isArray(data.similarItems)) data.similarItems.forEach(mapProductImages);

                supplierResults[supplierId] = {
                    bestMatch: data.best_match || data.bestMatch || null,
                    similarItems: data.similar_items || data.similarItems || []
                };
            }
        });

        // Process local suppliers (supplierWithRateCard)
        if (response.supplierWithRateCard && typeof response.supplierWithRateCard === 'object') {
            Object.keys(response.supplierWithRateCard).forEach(rateCardKey => {
                const supplierData = response.supplierWithRateCard![rateCardKey];
                const bestMatch = supplierData.bestMatch;
                const similarItems = supplierData.similarItems || [];
                const supplierName = (supplierData as any).supplierName || this.resolveSupplierName(rateCardKey);

                if (bestMatch || (Array.isArray(similarItems) && similarItems.length > 0)) {
                    const allItems = [...(Array.isArray(similarItems) ? similarItems : [])];
                    const getItemId = (item: any) => item?.sku_id || item?.skuId || item?.itemCode || item?.item_name || item?.itemName || '';
                    if (bestMatch && !allItems.find((i: any) => getItemId(i) === getItemId(bestMatch))) {
                        allItems.unshift(bestMatch);
                    }

                    allItems.forEach((item: any) => {
                        item.supplierName = supplierName;
                        item.platform = 'local';
                        item.supplierId = (supplierData as any).supplierId || rateCardKey;
                    });

                    const existingLocal = supplierResults['local'];
                    if (existingLocal) {
                        existingLocal.similarItems.push(...allItems);
                    } else {
                        supplierResults['local'] = {
                            bestMatch: bestMatch || null,
                            similarItems: allItems
                        };
                    }
                }
            });
        }

        // Store the server-computed best overall match
        if (response.bestOverallMatch) {
            searchItem.bestMatch = this.normalizeItem(response.bestOverallMatch, response.bestOverallMatch.platform || 'api', {});
        }

        // Map results into supplier sections
        Object.keys(supplierResults).forEach(supplierId => {
            const { bestMatch, similarItems } = supplierResults[supplierId];
            const section = this.supplierSections.find(s => s.id === supplierId);

            if (!section) {
                if (similarItems.length > 0 || bestMatch) {
                    const newSection: SupplierSection = {
                        id: supplierId,
                        name: supplierId.charAt(0).toUpperCase() + supplierId.slice(1),
                        color: '#f0f0f0',
                        items: [],
                        totalItems: 0,
                        isVisible: true,
                        isLocalSupplier: supplierId === 'local'
                    };
                    this.supplierSections.push(newSection);
                    this.addItemsToSection(newSection, bestMatch, similarItems, searchItem, {});
                }
                return;
            }

            this.addItemsToSection(section, bestMatch, similarItems, searchItem, {});
        });

        this.updateMarketBestPrice();
        this.updateSearchItemPrices(searchItem);
    }

    /**
     * Adds items to a supplier section.
     */
    private addItemsToSection(section: SupplierSection, bestMatch: any, similarItems: any[], searchItem: SearchItem, productImages: any = {}): void {
        const items: SupplierProduct[] = [];
        let normalizedBestMatchSkuId: string | null = null;

        if (bestMatch) {
            const normalized = this.normalizeItem(bestMatch, section.id, productImages);
            normalized.isBestMatch = true;
            normalized.searchItemName = searchItem.name;
            normalizedBestMatchSkuId = normalized.sku_id;
            items.push(normalized);
        }

        if (Array.isArray(similarItems)) {
            similarItems.forEach(item => {
                const normalized = this.normalizeItem(item, section.id, productImages);
                normalized.searchItemName = searchItem.name;
                if (normalizedBestMatchSkuId && normalized.sku_id === normalizedBestMatchSkuId) {
                    return;
                }
                items.push(normalized);
            });
        }

        // Show all items with a name, even if rate is 0
        const validItems = items.filter(item =>
            item.item_name
        );

        // Sync with Hyperpure cart
        const hpCartItems = this.hyperpureSupplier?.apiCartItems || [];
        if (section.id === 'hyperpure' && hpCartItems.length > 0) {
            const cartMap = new Map<string, number>();
            hpCartItems.forEach(ci => {
                cartMap.set(String(ci.sku_id), ci.selectedQuantity || ci.quantity || 0);
            });
            validItems.forEach(item => {
                const cartQty = cartMap.get(String(item.sku_id));
                if (cartQty && cartQty > 0) {
                    item.selectedQuantity = cartQty;
                    this.applyBulkRate(item);
                }
            });
        }

        // Sync with DMart cart
        const dmartQtyMap = this.dmartSupplier?.cartQuantityMap;
        if (section.id === 'dmart' && dmartQtyMap && dmartQtyMap.size > 0) {
            validItems.forEach(item => {
                const cartQty = dmartQtyMap.get(String(item.sku_id));
                if (cartQty && cartQty > 0) {
                    item.selectedQuantity = cartQty;
                    this.applyBulkRate(item);
                }
            });
        }

        section.items.push(...validItems);
        section.totalItems = section.items.length;
    }

    /**
     * Normalizes a raw item into a consistent SupplierProduct.
     */
    private normalizeItem(item: any, supplierId: string, productImages: any = {}): SupplierProduct {
        const rawItemId = item.sku_id || item.skuId || item.itemCode || item.item_code || item.item_name || item.itemName || '';
        const itemId = String(rawItemId).trim();
        const sId = (supplierId || item.platform || '').toLowerCase();

        let imageUrl = '';
        const itemProductImages = item.productImages || item.product_images;

        if (itemProductImages && typeof itemProductImages === 'string') {
            imageUrl = itemProductImages;
        } else if (Array.isArray(itemProductImages) && itemProductImages.length > 0) {
            imageUrl = itemProductImages[0];
        } else if (productImages && sId && productImages[sId] && productImages[sId][itemId]) {
            imageUrl = productImages[sId][itemId];
        } else if (productImages && productImages[itemId]) {
            imageUrl = productImages[itemId];
        }

        if (!imageUrl) {
            imageUrl = item.image_url || item.imageUrl || item.image || '';
        }

        const baseRate = Math.round(Number(item.rate || item.price || 0) * 100) / 100;
        const rawBulkPrices: BulkPrice[] = (item.bulkPrices || [])
            .map((bp: any) => ({
                qty: Number(bp.qty || bp.quantity || 0),
                price: Number(bp.price || bp.pricePerUnit || 0),
                type: bp.type || ''
            }))
            .filter((bp: BulkPrice) => bp.qty > 1 && bp.price > 0)
            .sort((a: BulkPrice, b: BulkPrice) => a.qty - b.qty);

        const rawAvlOrderQty = item.avlOrderQty;
        const parsedAvlOrderQty = (rawAvlOrderQty !== undefined && rawAvlOrderQty !== null && rawAvlOrderQty !== '' && !isNaN(Number(rawAvlOrderQty)))
            ? Number(rawAvlOrderQty)
            : undefined;

        const rawMaxOrderQty = item.maximumOrderQty;
        const parsedMaxOrderQty = (rawMaxOrderQty && rawMaxOrderQty < 2147483647) ? rawMaxOrderQty : undefined;

        let effectiveMaxQty: number | undefined = parsedMaxOrderQty;
        if (parsedAvlOrderQty !== undefined) {
            effectiveMaxQty = effectiveMaxQty ? Math.min(effectiveMaxQty, parsedAvlOrderQty) : parsedAvlOrderQty;
        }

        return {
            item_name: item.item_name || item.itemName || item.name || '',
            sku_id: itemId,
            rate: baseRate,
            baseRate: baseRate,
            mrp: Number(item.mrp || item.original_price || item.originalPrice || item.rate || 0),
            price: Number(item.price || item.rate || 0),
            unit: item.unit || 'kg',
            quantity: Number(item.quantity || item.qty || 1),
            image_url: imageUrl,
            product_images: typeof itemProductImages === 'string' ? itemProductImages : '',
            platform: item.platform || supplierId,
            isBestMatch: false,
            is_in_stock: item.isInStock !== undefined ? item.isInStock : true,
            bulkPrices: rawBulkPrices,
            supplierName: item.supplierName || '',
            supplierId: item.supplierId || '',
            category_id: item.categoryId || '',
            vertical: item.vertical || '',
            minimumOrderQty: item.minimumOrderQty || undefined,
            maximumOrderQty: effectiveMaxQty,
            avlOrderQty: parsedAvlOrderQty,
            product_number: item.product_number || item.productNumber || ''
        };
    }

    /**
     * Check if all searches are complete.
     */
    private checkAllSearchesComplete(): void {
        const allDone = this.searchItems.every(item => !item.isLoading);
        if (allDone) {
            this.isLoading = false;
        }
    }

    /**
     * Updates market best price display.
     */
    private updateMarketBestPrice(): void {
        this.refreshMarketBestPriceForActiveSearch();
    }

    /**
     * Refreshes the market best price for active search item.
     */
    refreshMarketBestPriceForActiveSearch(): void {
        const activeItem = this.searchItems.find(item => item.name === this.activeSearchItem);

        if (activeItem?.bestMatch) {
            const best = activeItem.bestMatch;

            if (!best.rate || best.rate <= 0) {
                this.marketBestPrice = null;
                return;
            }

            const section = this.supplierSections.find(s => s.id.toLowerCase() === (best.platform || '').toLowerCase())
                || this.supplierSections.find(s => s.id === 'local');

            const allPrices: number[] = [];
            let nextCheapest: SupplierProduct | null = null;

            this.supplierSections.forEach(sec => {
                this.getItemsForActiveSearch(sec).forEach(item => {
                    if (item.rate > 0) {
                        allPrices.push(item.rate);
                        if (item.rate !== best.rate) {
                            if (!nextCheapest || item.rate < nextCheapest.rate) {
                                nextCheapest = item;
                            }
                        }
                    }
                });
            });

            const savingsFromNextBest = nextCheapest ? nextCheapest.rate - best.rate : 0;
            const otherPrices = allPrices.filter(price => price !== best.rate);
            const avgMarketRate = otherPrices.length > 0
                ? otherPrices.reduce((sum, price) => sum + price, 0) / otherPrices.length
                : 0;
            const savingsPercent = avgMarketRate > 0
                ? Math.round(((avgMarketRate - best.rate) / avgMarketRate) * 100)
                : 0;

            this.marketBestPrice = {
                item_name: best.item_name,
                supplier: section ? `From ${section.name}` : (best.platform === 'supplier_rate_card' ? 'From Local Suppliers' : ''),
                supplierId: section?.id || (best.platform === 'supplier_rate_card' ? 'local' : undefined),
                rate: best.rate,
                mrp: best.mrp,
                unit: best.unit,
                image_url: best.image_url,
                product_images: best.product_images || '',
                savings: savingsFromNextBest > 0 ? `₹${savingsFromNextBest.toFixed(0)} cheaper than next best` : undefined,
                savingsPercent: savingsPercent > 0 ? savingsPercent : undefined,
                originalItem: best,
                originalSection: section
            };
            return;
        }

        // Fallback: find cheapest across all sections
        let cheapest: SupplierProduct | null = null;
        let cheapestSection: SupplierSection | null = null;

        this.supplierSections.forEach(section => {
            this.getItemsForActiveSearch(section).forEach(item => {
                if (item.rate > 0 && (!cheapest || item.rate < cheapest.rate)) {
                    cheapest = item;
                    cheapestSection = section;
                }
            });
        });

        if (cheapest && cheapestSection) {
            const allPrices: number[] = [];
            let nextCheapest: SupplierProduct | null = null;

            this.supplierSections.forEach(section => {
                this.getItemsForActiveSearch(section).forEach(item => {
                    if (item.rate > 0) {
                        allPrices.push(item.rate);
                        if (item !== cheapest) {
                            if (!nextCheapest || item.rate < nextCheapest.rate) {
                                nextCheapest = item;
                            }
                        }
                    }
                });
            });

            const savingsFromNextBest = nextCheapest ? nextCheapest.rate - cheapest.rate : 0;
            const otherPrices = allPrices.filter(price => price !== cheapest!.rate);
            const avgMarketRate = otherPrices.length > 0
                ? otherPrices.reduce((sum, price) => sum + price, 0) / otherPrices.length
                : 0;
            const savingsPercent = avgMarketRate > 0
                ? Math.round(((avgMarketRate - cheapest.rate) / avgMarketRate) * 100)
                : 0;

            this.marketBestPrice = {
                item_name: cheapest.item_name,
                supplier: `From ${cheapestSection.name}`,
                supplierId: cheapestSection.id,
                rate: cheapest.rate,
                mrp: cheapest.mrp,
                unit: cheapest.unit,
                image_url: cheapest.image_url,
                product_images: cheapest.product_images || '',
                savings: savingsFromNextBest > 0 ? `₹${savingsFromNextBest.toFixed(0)} cheaper than next best` : undefined,
                savingsPercent: savingsPercent > 0 ? savingsPercent : undefined,
                originalItem: cheapest,
                originalSection: cheapestSection
            };
        } else {
            this.marketBestPrice = null;
        }
    }

    /**
     * Updates search item prices.
     */
    private updateSearchItemPrices(searchItem: SearchItem): void {
        let minPrice = Infinity;
        let maxPrice = 0;

        this.supplierSections.forEach(section => {
            section.items
                .filter(item => item.searchItemName === searchItem.name)
                .forEach(item => {
                    if (item.rate > 0) {
                        minPrice = Math.min(minPrice, item.rate);
                        maxPrice = Math.max(maxPrice, item.rate);
                    }
                });
        });

        if (minPrice !== Infinity) {
            searchItem.minPrice = minPrice;
            searchItem.maxPrice = maxPrice;
            searchItem.priceRange = `₹${minPrice.toFixed(0)} - ₹${maxPrice.toFixed(0)}`;
        }
    }

    // ─────────────────────────────────────────────────────────────
    // UI Event Handlers - Same as original component
    // ─────────────────────────────────────────────────────────────

    toggleCardSelection(item: SupplierProduct, event?: Event): void {
        if (event) event.stopPropagation();
        if (this.selectedCard && this.selectedCard !== item) {
            this.selectedCard.isSelected = false;
        }
        item.isSelected = !item.isSelected;
        this.selectedCard = item.isSelected ? item : null;
    }

    openDrawer(section: SupplierSection, item?: SupplierProduct): void {
        this.selectedSection = section;
        this.isDrawerOpen = true;
        this.drawerSearchText = '';
        this.drawerSortOption = 'relevance';
        document.body.style.overflow = 'hidden';

        if (this.isMobileView && this.isSidebarOpen) {
            this.isSidebarOpen = false;
        }

        if (item) {
            if (section.id === 'dmart' || section.id === 'hyperpure') {
                this.addToCart(item, section);
            } else {
                let targetItem = section.items.find(i => i === item);
                if (!targetItem && item.sku_id) {
                    targetItem = section.items.find(i => i.sku_id === item.sku_id);
                }
                const finalItem = targetItem || item;
                finalItem.selectedQuantity = (finalItem.selectedQuantity || 0) + 1;
                this.sharedService.successAlert(`Added "${finalItem.item_name}" to cart`);
            }
        }
    }

    closeDrawer(): void {
        this.isDrawerOpen = false;
        if (!this.isSidebarOpen || !this.isMobileView) {
            document.body.style.overflow = '';
        }
        setTimeout(() => {
            this.selectedSection = null;
        }, 300);
    }

    getEffectiveRate(item: SupplierProduct, qty?: number): number {
        const quantity = qty !== undefined ? qty : (item.selectedQuantity || 1);
        const baseRate = item.baseRate || item.rate;
        if (!item.bulkPrices || item.bulkPrices.length === 0) {
            return baseRate;
        }
        let effectivePrice = baseRate;
        for (const bp of item.bulkPrices) {
            if (quantity >= bp.qty) {
                effectivePrice = bp.price;
            } else {
                break;
            }
        }
        return effectivePrice;
    }

    private applyBulkRate(item: SupplierProduct): void {
        if (item.bulkPrices && item.bulkPrices.length > 0) {
            item.rate = this.getEffectiveRate(item);
        }
    }

    getNextBulkTier(item: SupplierProduct): BulkPrice | null {
        if (!item.bulkPrices || item.bulkPrices.length === 0) return null;
        const qty = item.selectedQuantity || 0;
        for (const bp of item.bulkPrices) {
            if (qty < bp.qty) {
                return bp;
            }
        }
        return null;
    }

    isActiveBulkTier(item: SupplierProduct, tier: BulkPrice): boolean {
        if (!item.bulkPrices || !item.selectedQuantity || item.selectedQuantity < tier.qty) {
            return false;
        }
        let activeTier: BulkPrice | null = null;
        for (const bp of item.bulkPrices) {
            if (item.selectedQuantity >= bp.qty) {
                activeTier = bp;
            } else {
                break;
            }
        }
        return activeTier === tier;
    }

    updateQuantity(item: SupplierProduct, change: number): void {
        if (item.isAddingToCart) return;

        if (!item.selectedQuantity) item.selectedQuantity = 0;
        const previousQuantity = item.selectedQuantity;

        if (change > 0 && item.maximumOrderQty && (item.selectedQuantity + change) > item.maximumOrderQty) {
            this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
            return;
        }

        item.selectedQuantity += change;
        if (item.selectedQuantity < 0) item.selectedQuantity = 0;

        if (change < 0 && item.minimumOrderQty && item.selectedQuantity > 0 && item.selectedQuantity < item.minimumOrderQty) {
            item.selectedQuantity = 0;
        }

        this.applyBulkRate(item);
        item.cartError = undefined;

        const action = change > 0 ? 'add' : 'remove';
        if (item.platform === 'hyperpure' && item.sku_id && this.hyperpureSupplier) {
            this.hyperpureSupplier.updateQuantity(item, change, previousQuantity);
            this.highlightCart('hyperpure', action);
        } else if (item.platform === 'dmart' && item.sku_id && this.dmartSupplier) {
            this.dmartSupplier.updateQuantity(item, change, previousQuantity);
            this.highlightCart('dmart', action);
        } else {
            if (change > 0 && item.selectedQuantity === 1) {
                this.sharedService.successAlert(`Added "${item.item_name}" to cart`);
            }
            this.highlightCart(item.platform || 'local', action);
        }
    }

    addToCart(item: any, section: any): void {
        if (item.isAddingToCart) return;

        const itemName = item?.item_name || 'Item';
        const supplierName = section?.name || item?.supplier || 'Supplier';

        if (item.rate) {
            const previousQuantity = item.selectedQuantity || 0;

            if (item.maximumOrderQty && (previousQuantity + 1) > item.maximumOrderQty) {
                this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
                return;
            }

            if (previousQuantity === 0 && item.minimumOrderQty && item.minimumOrderQty > 1) {
                item.selectedQuantity = item.minimumOrderQty;
            } else {
                item.selectedQuantity = previousQuantity + 1;
            }
            this.applyBulkRate(item);
            item.cartError = undefined;

            if (item.platform === 'hyperpure' && item.sku_id && this.hyperpureSupplier) {
                this.hyperpureSupplier.addToCart(item, supplierName, previousQuantity);
                this.highlightCart('hyperpure', 'add');
            } else if (item.platform === 'dmart' && item.sku_id && this.dmartSupplier) {
                this.dmartSupplier.addToCart(item, supplierName, previousQuantity);
                this.highlightCart('dmart', 'add');
            } else {
                this.sharedService.successAlert(`Added "${itemName}" from ${supplierName} to cart`);
                this.highlightCart(section?.id || 'local', 'add');
            }
        }
    }

    highlightCart(platform: string, action: 'add' | 'remove' = 'add'): void {
        if (this.highlightTimeout) {
            clearTimeout(this.highlightTimeout);
        }
        this.highlightedCartPlatform = platform;
        this.cartHighlightAction = action;
        this.highlightTimeout = setTimeout(() => {
            this.highlightedCartPlatform = null;
            this.cartHighlightAction = null;
        }, 1200);
    }

    openBestPriceDrawer(): void {
        if (this.marketBestPrice?.originalSection && this.marketBestPrice?.originalItem) {
            this.openDrawer(this.marketBestPrice.originalSection, this.marketBestPrice.originalItem);
        } else if (this.marketBestPrice?.supplierId) {
            const section = this.supplierSections.find(s => s.id === this.marketBestPrice?.supplierId);
            if (section) {
                this.openDrawer(section);
            }
        }
    }

    // Quick Add functionality
    onQuickAddClick(category: { label: string; icon: string; key: string; active: boolean; addedItems: string[] }): void {
        if (category.active) {
            category.active = false;
            localStorage.removeItem(`pm_activeQuickAdd_${this.selectedEntityId}`);
            return;
        }

        this.quickAddCategories.forEach(c => c.active = false);
        category.active = true;
        localStorage.setItem(`pm_activeQuickAdd_${this.selectedEntityId}`, category.key);

        this.isQuickAddLoading = true;

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        const params = {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            dateField: 'invoiceDate'
        };

        let obs$;
        switch (category.key) {
            case 'mostPurchased': obs$ = this.directPriceDiscoveryService.getMostPurchasedItems(params, this.selectedEntityId); break;
            case 'recentPurchase': obs$ = this.directPriceDiscoveryService.getRecentPurchaseItems(params, this.selectedEntityId); break;
            case 'lastSearched': obs$ = this.directPriceDiscoveryService.getLastSearchedItems(params, this.selectedEntityId); break;
            case 'mostSearched': obs$ = this.directPriceDiscoveryService.getMostSearchedItems(params, this.selectedEntityId); break;
            case 'topOrdered': obs$ = this.directPriceDiscoveryService.getTopOrderedItems(params, this.selectedEntityId); break;
            default: this.isQuickAddLoading = false; return;
        }

        obs$.pipe(takeUntil(this.destroy$)).subscribe(
            (items: any[]) => {
                this.isQuickAddLoading = false;
                const names: string[] = (items || []).slice(0, 10)
                    .map((item: any) => (item.itemName || item.item_name || item.name || '').trim())
                    .filter((n: string) => !!n);

                if (names.length === 0) {
                    this.sharedService.warningAlert(`No items found for "${category.label}"`);
                    category.active = false;
                    return;
                }

                const addedNames: string[] = [];
                names.forEach(name => {
                    const exists = this.searchItems.find(i => i.name.toLowerCase() === name.toLowerCase());
                    if (!exists) {
                        const newItem: SearchItem = { name, isLoading: true };
                        this.searchItems.push(newItem);
                        this.directSearchItem(newItem);
                        addedNames.push(name);
                    }
                });

                category.addedItems = addedNames;

                if (this.searchItems.length > 0) {
                    this.selectSearchItem(this.searchItems[0]);
                }

                const currentTags = this.sharedService.encryptForUrl(
                    this.searchItems.map(i => i.name).join('$$,$$')
                );
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { tags: currentTags },
                    queryParamsHandling: 'merge'
                });

                this.sharedService.successAlert(`${names.length} item${names.length > 1 ? 's' : ''} from "${category.label}" added`);
            },
            (error: any) => {
                console.error(`Quick Add failed for ${category.key}`, error);
                this.isQuickAddLoading = false;
                category.active = false;
                this.sharedService.errorAlert(`Failed to load ${category.label}`);
            }
        );
    }

    addNewSearchItem(): void {
        const searchTerm = (this.newItemSearchText || '').trim();
        if (!searchTerm) return;

        const existingItem = this.searchItems.find(
            i => i.name.toLowerCase() === searchTerm.toLowerCase()
        );

        if (existingItem) {
            this.selectSearchItem(existingItem);
            this.newItemSearchText = '';
            return;
        }

        const newItem: SearchItem = { name: searchTerm, isLoading: true };
        this.searchItems.push(newItem);

        const currentTags = this.sharedService.encryptForUrl(
            this.searchItems.map(i => i.name).join('$$,$$')
        );
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tags: currentTags },
            queryParamsHandling: 'merge'
        });

        this.selectSearchItem(newItem);
        this.directSearchItem(newItem);
        this.newItemSearchText = '';
    }

    removeSearchItem(searchItem: SearchItem): void {
        this.supplierSections.forEach(section => {
            section.items = section.items.filter(item => item.searchItemName !== searchItem.name);
            section.totalItems = section.items.length;
        });

        this.searchItems = this.searchItems.filter(i => i.name !== searchItem.name);

        this.quickAddCategories.forEach(cat => {
            if (cat.active && cat.addedItems && cat.addedItems.length > 0) {
                const anyRemaining = cat.addedItems.some(
                    n => this.searchItems.some(i => i.name.toLowerCase() === n.toLowerCase())
                );
                if (!anyRemaining) {
                    cat.active = false;
                    cat.addedItems = [];
                    localStorage.removeItem(`pm_activeQuickAdd_${this.selectedEntityId}`);
                }
            }
        });

        if (this.searchItems.length === 0) {
            this.goBack();
        } else {
            if (this.activeSearchItem === searchItem.name) {
                this.selectSearchItem(this.searchItems[0]);
            } else {
                this.refreshMarketBestPriceForActiveSearch();
            }
        }
    }

    removeQuickAddItems(category: { label: string; icon: string; key: string; active: boolean; addedItems: string[] }): void {
        if (!category.addedItems || category.addedItems.length === 0) {
            category.active = false;
            return;
        }

        const namesToRemove = new Set(category.addedItems.map(n => n.toLowerCase()));
        const itemsToRemove = this.searchItems.filter(i => namesToRemove.has(i.name.toLowerCase()));

        itemsToRemove.forEach(searchItem => {
            this.supplierSections.forEach(section => {
                section.items = section.items.filter(item => item.searchItemName !== searchItem.name);
                section.totalItems = section.items.length;
            });
        });

        this.searchItems = this.searchItems.filter(i => !namesToRemove.has(i.name.toLowerCase()));

        category.active = false;
        category.addedItems = [];
        localStorage.removeItem(`pm_activeQuickAdd_${this.selectedEntityId}`);

        if (this.searchItems.length === 0) {
            this.marketBestPrice = null;
            this.activeSearchItem = '';
        } else {
            if (!this.searchItems.find(i => i.name === this.activeSearchItem)) {
                this.selectSearchItem(this.searchItems[0]);
            } else {
                this.refreshMarketBestPriceForActiveSearch();
            }
        }

        if (this.searchItems.length > 0) {
            const currentTags = this.sharedService.encryptForUrl(
                this.searchItems.map(i => i.name).join('$$,$$')
            );
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { tags: currentTags },
                queryParamsHandling: 'merge'
            });
        } else {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { tags: null },
                queryParamsHandling: 'merge'
            });
        }
    }

    isStandardSection(sectionId: string): boolean {
        return ['hyperpure', 'dmart', 'udaan', 'local'].includes(sectionId);
    }

    selectSearchItem(searchItem: SearchItem): void {
        this.activeSearchItem = searchItem.name;

        if (searchItem.name !== '__fav__') {
            this.isFavViewMode = false;
        }

        this.refreshMarketBestPriceForActiveSearch();

        if (this.isMobileView && this.isSidebarOpen) {
            this.isSidebarOpen = false;
            document.body.style.overflow = '';
        }
    }

    goBack(): void {
        this.router.navigate(['/purchase-manager']);
    }

    // ─────────────────────────────────────────────────────────────
    // Cart Management - Same as original
    // ─────────────────────────────────────────────────────────────

    get cartSections(): any[] {
        const carts: any[] = [];
        let hasHpCartFromSearch = false;
        let hasDmartCartFromSearch = false;

        this.supplierSections.forEach(section => {
            const cartItems = section.items.filter(item => item.selectedQuantity && item.selectedQuantity > 0);
            if (cartItems.length > 0) {
                if (section.id === 'local') {
                    const localGroups = new Map<string, any[]>();
                    cartItems.forEach(item => {
                        const sName = item.supplierName || 'LOCAL SUPPLIERS';
                        if (!localGroups.has(sName)) localGroups.set(sName, []);
                        localGroups.get(sName)!.push(item);
                    });

                    localGroups.forEach((items, sName) => {
                        const totalValue = items.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                        carts.push({
                            section: section,
                            platform: 'local',
                            supplierName: sName,
                            totalValue: totalValue,
                            totalCount: items.length,
                            items: items
                        });
                    });
                } else if (section.id === 'hyperpure') {
                    const hpCartCount = this.hyperpureSupplier?.apiCartCount || 0;
                    const totalValue = cartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                    carts.push({
                        section: section,
                        platform: section.id,
                        supplierName: section.name,
                        totalValue: totalValue,
                        totalCount: hpCartCount > 0 ? hpCartCount : cartItems.length,
                        items: cartItems
                    });
                    hasHpCartFromSearch = true;
                } else if (section.id === 'dmart') {
                    const dmartCartCount = this.dmartSupplier?.apiCartCount || 0;
                    const totalValue = cartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                    carts.push({
                        section: section,
                        platform: section.id,
                        supplierName: section.name,
                        totalValue: totalValue,
                        totalCount: dmartCartCount > 0 ? dmartCartCount : cartItems.length,
                        items: cartItems
                    });
                    hasDmartCartFromSearch = true;
                } else {
                    const totalValue = cartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                    carts.push({
                        section: section,
                        platform: section.id,
                        supplierName: section.name,
                        totalValue: totalValue,
                        totalCount: cartItems.length,
                        items: cartItems
                    });
                }
            }
        });

        // Handle Hyperpure API cart
        const hpApiCartCount = this.hyperpureSupplier?.apiCartCount || 0;
        const hpApiCartItems = this.hyperpureSupplier?.apiCartItems || [];
        if (!hasHpCartFromSearch && hpApiCartCount > 0) {
            const hpSection = this.supplierSections.find(s => s.id === 'hyperpure');
            const totalValue = hpApiCartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
            carts.unshift({
                section: hpSection || { id: 'hyperpure', name: 'Hyperpure', items: [] },
                platform: 'hyperpure',
                supplierName: 'Hyperpure',
                totalValue: totalValue,
                totalCount: hpApiCartCount,
                items: hpApiCartItems
            });
        }

        // Handle DMart API cart
        const dmartApiCartCount = this.dmartSupplier?.apiCartCount || 0;
        const dmartApiCartItems = this.dmartSupplier?.apiCartItems || [];
        if (!hasDmartCartFromSearch && dmartApiCartCount > 0) {
            const dmartSection = this.supplierSections.find(s => s.id === 'dmart');
            const totalValue = dmartApiCartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
            carts.push({
                section: dmartSection || { id: 'dmart', name: 'DMart', items: [] },
                platform: 'dmart',
                supplierName: 'DMart',
                totalValue: totalValue,
                totalCount: dmartApiCartCount,
                items: dmartApiCartItems
            });
        }

        return carts;
    }

    get totalCartValue(): number {
        return this.cartSections.reduce((sum, cart) => sum + cart.totalValue, 0);
    }

    get hasCartItems(): boolean {
        const hpCartCount = this.hyperpureSupplier?.apiCartCount || 0;
        const dmartCartCount = this.dmartSupplier?.apiCartCount || 0;
        return this.cartSections.length > 0 || hpCartCount > 0 || dmartCartCount > 0;
    }

    scrollCarts(direction: 'left' | 'right'): void {
        const container = document.querySelector('.supplier-carts-container') as HTMLElement;
        if (container) {
            const scrollAmount = 300;
            if (direction === 'left') {
                container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            } else {
                container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            }
        }
    }

    openCheckoutDrawer(cart: any): void {
        this.selectedCheckoutCart = cart;
        this.isCheckoutDrawerOpen = true;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
    }

    closeCheckoutDrawer(): void {
        this.isCheckoutDrawerOpen = false;
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        setTimeout(() => {
            this.selectedCheckoutCart = null;
        }, 300);
    }

    checkoutSupplier(cart: any): void {
        this.openCheckoutDrawer(cart);
    }

    createPO(cart: any): void {
        const supplierId = cart.section?.items[0]?.supplierId || cart.items[0]?.supplierId;
        const fullSupplier = this.allSuppliers.find(s => (s.id || s._id) === supplierId);
        const enrichedSupplier = {
            id: supplierId,
            name: this.resolveSupplierName(cart.supplierName),
            alias: fullSupplier?.alias || fullSupplier?.supplier_alias || '',
            supplierAlias: fullSupplier?.supplierAlias || fullSupplier?.supplier_alias || '',
            gstNo: fullSupplier?.gstNo || fullSupplier?.gst_no || fullSupplier?.gstNumber || '',
            mobile: fullSupplier?.mobile || fullSupplier?.mobile_number || fullSupplier?.mobileNumber || '',
            email: fullSupplier?.email || ''
        };

        this.localSupplierPOModal.open(enrichedSupplier, cart.items);
    }

    onPOGenerated(poData: any): void {
        this.sharedService.successAlert(`Purchase Order ${poData.poNumber} created successfully!`);
        const supplierId = poData.supplierId;
        this.supplierSections.forEach(section => {
            if (section.id === 'local') {
                section.items.forEach(item => {
                    if (item.platform === 'local' && (this.suppliersMap.get(item.supplierName || '') === poData.supplierName || item.supplierName === poData.supplierName || item.supplierId === supplierId)) {
                        item.selectedQuantity = 0;
                    }
                });
            }
        });
    }

    proceedToBuy(): void {
        if (!this.selectedCheckoutCart) return;

        const platform = this.selectedCheckoutCart.platform;

        if (platform === 'local') {
            this.sharedService.successAlert(`Proceeding to create PO for ${this.selectedCheckoutCart.supplierName}`);
            this.closeCheckoutDrawer();
        } else if (platform === 'hyperpure') {
            this.closeCheckoutDrawer();
            if (this.hyperpureSupplier) {
                this.hyperpureSupplier.proceedToBuy();
            }
        } else if (platform === 'dmart') {
            this.closeCheckoutDrawer();
            if (this.dmartSupplier) {
                this.dmartSupplier.proceedToBuy(this.selectedCheckoutCart.items);
            }
        } else if (platform === 'udaan') {
            const items = this.selectedCheckoutCart.items;
            if (items.length === 0) return;
            this.closeCheckoutDrawer();
            if (this.udaanSupplier) {
                const udaanSection = this.supplierSections.find(s => s.id === 'udaan');
                this.udaanSupplier.entityId = this.selectedEntityId;
                this.udaanSupplier.storeUuid = this.udaanStoreUuid;
                this.udaanSupplier.supplierSection = udaanSection || null;
                this.udaanSupplier.proceedToBuy(items);
            }
        } else {
            this.sharedService.successAlert(`Proceeding to checkout for ${this.selectedCheckoutCart.supplierName}`);
            this.closeCheckoutDrawer();
        }
    }

    updateCheckoutQty(item: SupplierProduct, delta: number): void {
        if (item.isAddingToCart) return;

        const previousQty = item.selectedQuantity || 1;

        if (delta > 0 && item.maximumOrderQty && (previousQty + delta) > item.maximumOrderQty) {
            this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
            return;
        }

        const newQty = previousQty + delta;
        if (newQty < 1) {
            item.selectedQuantity = 0;
        } else if (delta < 0 && item.minimumOrderQty && newQty < item.minimumOrderQty) {
            item.selectedQuantity = 0;
        } else {
            item.selectedQuantity = newQty;
        }
        this.applyBulkRate(item);
        item.cartError = undefined;

        if (item.platform === 'hyperpure' && item.sku_id && this.hyperpureSupplier) {
            this.hyperpureSupplier.syncCartLocally(String(item.sku_id));
            this.refreshCheckoutCart();

            item.isAddingToCart = true;
            const productId = Number(item.sku_id);
            const quantity = item.selectedQuantity;
            const deliveryMode = 'DELIVERY';

            this.priceDiscoveryService.addToCart(productId, quantity, deliveryMode, this.selectedEntityId)
                .subscribe({
                    next: () => {
                        item.isAddingToCart = false;
                    },
                    error: (error) => {
                        item.isAddingToCart = false;
                        item.selectedQuantity = previousQty;
                        this.hyperpureSupplier?.syncCartLocally(String(item.sku_id));
                        const errorMessage = error?.error?.message || error?.message || 'Failed to update quantity.';
                        item.cartError = errorMessage;
                        this.sharedService.errorAlert(errorMessage);
                        this.refreshCheckoutCart();
                    }
                });
        } else if (item.platform === 'dmart' && item.sku_id && this.dmartSupplier) {
            this.dmartSupplier.syncCartLocally(String(item.sku_id));
            this.refreshCheckoutCart();

            item.isAddingToCart = true;
            this.priceDiscoveryService.addToDmartCart(String(item.sku_id), item.selectedQuantity, this.selectedEntityId)
                .subscribe({
                    next: () => {
                        item.isAddingToCart = false;
                    },
                    error: (error) => {
                        item.isAddingToCart = false;
                        item.selectedQuantity = previousQty;
                        this.dmartSupplier?.syncCartLocally(String(item.sku_id));
                        const errorMessage = error?.error?.message || error?.message || 'Failed to update quantity.';
                        item.cartError = errorMessage;
                        this.sharedService.errorAlert(errorMessage);
                        this.refreshCheckoutCart();
                    }
                });
        } else {
            this.refreshCheckoutCart();
        }
    }

    onCheckoutQtyChange(item: SupplierProduct, event: any): void {
        const previousQty = item.selectedQuantity || 1;
        let val = parseInt(event.target.value, 10);

        if (item.maximumOrderQty && val > item.maximumOrderQty) {
            val = item.maximumOrderQty;
            event.target.value = val;
            this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
        }

        if (item.minimumOrderQty && val > 0 && val < item.minimumOrderQty) {
            val = item.minimumOrderQty;
            event.target.value = val;
            this.sharedService.warningAlert(`Minimum order quantity is ${item.minimumOrderQty}`);
        }

        if (val && val > 0) {
            item.selectedQuantity = val;
        } else {
            item.selectedQuantity = 1;
            event.target.value = 1;
        }

        this.applyBulkRate(item);

        if (item.platform === 'hyperpure' && item.sku_id && this.hyperpureSupplier) {
            this.hyperpureSupplier.syncCartLocally(String(item.sku_id));
            this.refreshCheckoutCart();

            const productId = Number(item.sku_id);
            const quantity = item.selectedQuantity;
            const deliveryMode = 'DELIVERY';

            this.priceDiscoveryService.addToCart(productId, quantity, deliveryMode, this.selectedEntityId)
                .subscribe({
                    next: () => { },
                    error: () => {
                        item.selectedQuantity = previousQty;
                        event.target.value = previousQty;
                        this.hyperpureSupplier?.syncCartLocally(String(item.sku_id));
                        this.refreshCheckoutCart();
                        this.sharedService.errorAlert('Failed to update quantity.');
                    }
                });
        } else if (item.platform === 'dmart' && item.sku_id && this.dmartSupplier) {
            this.dmartSupplier.syncCartLocally(String(item.sku_id));
            this.refreshCheckoutCart();

            this.priceDiscoveryService.addToDmartCart(String(item.sku_id), item.selectedQuantity, this.selectedEntityId)
                .subscribe({
                    next: () => { },
                    error: () => {
                        item.selectedQuantity = previousQty;
                        event.target.value = previousQty;
                        this.dmartSupplier?.syncCartLocally(String(item.sku_id));
                        this.refreshCheckoutCart();
                        this.sharedService.errorAlert('Failed to update quantity.');
                    }
                });
        } else {
            this.refreshCheckoutCart();
        }
    }

    private refreshCheckoutCart(): void {
        if (!this.selectedCheckoutCart) return;

        const allItems = this.selectedCheckoutCart.items || [];
        allItems.forEach((item: SupplierProduct) => {
            if ((!item.selectedQuantity || item.selectedQuantity <= 0) && this.marketBestItemSkus.has(String(item.sku_id))) {
                this.marketBestItemSkus.delete(String(item.sku_id));
                this.marketBestItemsSnapshot = this.marketBestItemsSnapshot.filter(
                    s => s.skuId !== String(item.sku_id)
                );
            }
        });

        const items = allItems.filter(
            (i: SupplierProduct) => i.selectedQuantity && i.selectedQuantity > 0
        );
        this.selectedCheckoutCart.items = items;
        this.selectedCheckoutCart.totalCount = items.length;
        this.selectedCheckoutCart.totalValue = items.reduce(
            (sum: number, i: SupplierProduct) => sum + (this.getEffectiveRate(i) * (i.selectedQuantity || 0)), 0
        );

        this.hasMarketBestItems = this.marketBestItemSkus.size > 0;
        this.saveMarketBestItems();

        if (items.length === 0) {
            this.closeCheckoutDrawer();
        }
    }

    getItemsForActiveSearch(section: SupplierSection): SupplierProduct[] {
        if (!this.activeSearchItem) return section.items;
        return section.items.filter(item => item.searchItemName === this.activeSearchItem);
    }

    calculateMaxVisibleCards(): void {
        const sidebarWidth = 220;
        const containerPadding = 64;
        const sectionPadding = 40;
        const cardWidth = 195;
        const cardGap = 16;
        const availableWidth = window.innerWidth - sidebarWidth - containerPadding - sectionPadding;
        const maxCards = Math.floor((availableWidth + cardGap) / (cardWidth + cardGap));
        this.maxVisibleCards = Math.max(3, maxCards);
    }

    checkMobileView(): void {
        this.isMobileView = window.innerWidth <= 768;
        if (!this.isMobileView) {
            this.isSidebarOpen = false;
        }
    }

    toggleMobileSidebar(): void {
        this.isSidebarOpen = !this.isSidebarOpen;

        if (this.isSidebarOpen && this.isDrawerOpen) {
            this.isDrawerOpen = false;
            this.selectedSection = null;
        }

        if (this.isSidebarOpen && this.isMobileView) {
            document.body.style.overflow = 'hidden';
        } else if (!this.isDrawerOpen) {
            document.body.style.overflow = '';
        }
    }

    getVisibleItems(section: SupplierSection): SupplierProduct[] {
        const allItems = this.getItemsForActiveSearch(section);
        const totalItems = allItems.length;
        const itemsToShow = Math.min(totalItems, this.maxVisibleCards);
        const items = allItems.slice(0, itemsToShow);

        items.forEach(item => {
            if (!item.image_url && item.product_images) {
                item.image_url = item.product_images;
            }
        });
        return items;
    }

    shouldShowViewAll(section: SupplierSection): boolean {
        return this.getActiveTotalItems(section) > this.maxVisibleCards;
    }

    getActiveTotalItems(section: SupplierSection): number {
        return this.getItemsForActiveSearch(section).length;
    }

    sectionHasActiveItems(section: SupplierSection): boolean {
        return this.getItemsForActiveSearch(section).length > 0;
    }

    viewFullCart(): void {
        this.sharedService.successAlert('Opening full cart view');
    }

    hasNoResults(): boolean {
        return this.supplierSections.every(s => this.getItemsForActiveSearch(s).length === 0);
    }

    getSupplierLogoUrl(supplierId: string): string {
        const supplier = this.supplierRegistry.get(supplierId);
        return supplier ? supplier.logoUrl : '';
    }

    onChildCartUpdate(event: CartUpdateEvent): void {
        if (this.isCheckoutDrawerOpen && this.selectedCheckoutCart?.platform === event.platform) {
            this.refreshCheckoutCart();
        }
    }

    onIframeClosed(): void {
        if (this.isCheckoutDrawerOpen) {
            this.refreshCheckoutCart();
        }
    }

    // ─────────────────────────────────────────────────────────────
    // Best Match Popup - Same as original
    // ─────────────────────────────────────────────────────────────

    openBestMatchPopup(): void {
        this.bestMatchCandidates = [];

        this.searchItems.forEach(searchItem => {
            if (searchItem.isLoading) return;

            if (searchItem.bestMatch && searchItem.bestMatch.is_in_stock !== false) {
                const best = searchItem.bestMatch;
                const platform = (best.platform || '').toLowerCase();
                const section = this.supplierSections.find(s => s.id.toLowerCase() === platform);
                const platformLabel = section ? section.name : (best.platform || 'Unknown');

                const liveItem = section
                    ? (section.items.find(i => String(i.sku_id) === String(best.sku_id) && i.searchItemName === searchItem.name)
                        || section.items.find(i => String(i.sku_id) === String(best.sku_id))
                        || best)
                    : best;

                const skuStr = String(liveItem.sku_id);
                const alreadyAdded = this.marketBestItemSkus.has(skuStr);
                const existingQty = liveItem.selectedQuantity || 0;
                const isInCart = existingQty > 0 && !alreadyAdded;
                const minQty = Number(liveItem.minimumOrderQty) || 1;

                this.bestMatchCandidates.push({
                    searchItemName: searchItem.name,
                    platform: section ? section.id : platform,
                    platformLabel,
                    item: liveItem,
                    isSelected: !alreadyAdded,
                    isAlreadyAdded: alreadyAdded,
                    isAlreadyInCart: isInCart,
                    existingCartQty: existingQty,
                    minimumOrderQty: minQty,
                    quantity: alreadyAdded ? (liveItem.selectedQuantity || minQty) : (isInCart ? 1 : minQty)
                });
                return;
            }

            let fallbackCandidate: BestMatchCandidate | null = null;
            this.supplierSections.forEach(section => {
                const matchItems = section.items.filter(item =>
                    item.searchItemName === searchItem.name && item.isBestMatch && item.is_in_stock
                );
                matchItems.forEach(item => {
                    const skuStr = String(item.sku_id);
                    const alreadyAdded = this.marketBestItemSkus.has(skuStr);
                    const existingQty = item.selectedQuantity || 0;
                    const isInCart = existingQty > 0 && !alreadyAdded;
                    const minQty = item.minimumOrderQty || 1;
                    const candidate: BestMatchCandidate = {
                        searchItemName: searchItem.name,
                        platform: section.id,
                        platformLabel: section.name,
                        item,
                        isSelected: !alreadyAdded,
                        isAlreadyAdded: alreadyAdded,
                        isAlreadyInCart: isInCart,
                        existingCartQty: existingQty,
                        minimumOrderQty: minQty,
                        quantity: alreadyAdded ? (item.selectedQuantity || minQty) : (isInCart ? 1 : minQty)
                    };
                    if (!fallbackCandidate || item.rate < fallbackCandidate.item.rate) {
                        fallbackCandidate = candidate;
                    }
                });
            });
            if (fallbackCandidate) {
                this.bestMatchCandidates.push(fallbackCandidate);
            }
        });

        if (this.bestMatchCandidates.length === 0) {
            this.sharedService.warningAlert('No best match items found.');
            return;
        }

        const pendingItems = this.bestMatchCandidates.filter(c => !c.isAlreadyAdded);
        this.selectAllBestMatches = pendingItems.length > 0 && pendingItems.every(c => c.isSelected);
        this.isBestMatchPopupOpen = true;
        document.body.style.overflow = 'hidden';
    }

    closeBestMatchPopup(): void {
        this.isBestMatchPopupOpen = false;
        document.body.style.overflow = '';
    }

    toggleSelectAllBestMatches(): void {
        this.selectAllBestMatches = !this.selectAllBestMatches;
        this.bestMatchCandidates.forEach(c => {
            if (!c.isAlreadyAdded) {
                c.isSelected = this.selectAllBestMatches;
            }
        });
    }

    onBestMatchSelectionChange(): void {
        const pending = this.bestMatchCandidates.filter(c => !c.isAlreadyAdded);
        this.selectAllBestMatches = pending.length > 0 && pending.every(c => c.isSelected);
    }

    updateBestMatchQty(candidate: BestMatchCandidate, delta: number): void {
        const newQty = candidate.quantity + delta;
        const effectiveMinQty = candidate.isAlreadyInCart ? 1 : candidate.minimumOrderQty;
        if (newQty < effectiveMinQty) return;
        const totalQty = candidate.existingCartQty + newQty;
        if (candidate.item.maximumOrderQty && totalQty > candidate.item.maximumOrderQty) {
            this.sharedService.warningAlert(`Maximum order quantity is ${candidate.item.maximumOrderQty}.`);
            return;
        }
        candidate.quantity = newQty;
    }

    get selectedBestMatchCount(): number {
        return this.bestMatchCandidates.filter(c => c.isSelected && !c.isAlreadyAdded).length;
    }

    get alreadyAddedBestMatchCount(): number {
        return this.bestMatchCandidates.filter(c => c.isAlreadyAdded).length;
    }

    get bestMatchTotalValue(): number {
        return this.bestMatchCandidates
            .filter(c => c.isSelected && !c.isAlreadyAdded)
            .reduce((sum, c) => sum + (c.item.rate * c.quantity), 0);
    }

    getPlatformLogo(platform: string): string {
        switch (platform) {
            case 'hyperpure': return 'assets/images/hyperpure-logo.png';
            case 'dmart': return 'assets/images/d-mart-logo.png';
            case 'udaan': return 'assets/images/udaan-logo.png';
            case 'local': return 'assets/images/local-suppliers-logo.png';
            default: return '';
        }
    }

    addAllBestMatches(): void {
        const selected = this.bestMatchCandidates.filter(c => c.isSelected && !c.isAlreadyAdded);
        if (selected.length === 0) {
            this.sharedService.warningAlert('Please select at least one item to add');
            return;
        }

        this.isAddingBestMatches = true;
        let completedCount = 0;
        let errorCount = 0;

        const onComplete = () => {
            completedCount++;
            if (completedCount === selected.length) {
                this.isAddingBestMatches = false;
                this.closeBestMatchPopup();
                this.hasMarketBestItems = this.marketBestItemSkus.size > 0;
                this.saveMarketBestItems();

                if (errorCount > 0) {
                    this.sharedService.warningAlert(`${selected.length - errorCount} of ${selected.length} items added.`);
                } else {
                    this.sharedService.successAlert(`${selected.length} Market Best items added!`);
                }
            }
        };

        selected.forEach(candidate => {
            const item = candidate.item;
            const section = this.supplierSections.find(s => s.id === candidate.platform);
            if (!section) {
                errorCount++;
                onComplete();
                return;
            }

            let targetItem = section.items.find(i => i.sku_id === item.sku_id && i.searchItemName === candidate.searchItemName);
            if (!targetItem) {
                targetItem = section.items.find(i => i.sku_id === item.sku_id);
            }
            if (!targetItem) {
                targetItem = item;
            }

            const previousQty = targetItem.selectedQuantity || 0;
            const snapshotExists = this.marketBestItemsSnapshot.some(s => s.skuId === String(targetItem!.sku_id));
            if (!snapshotExists) {
                this.marketBestItemsSnapshot.push({
                    platform: candidate.platform,
                    skuId: String(targetItem.sku_id),
                    previousQty: previousQty
                });
            }

            this.marketBestItemSkus.add(String(targetItem.sku_id));

            const newQty = previousQty + candidate.quantity;
            targetItem.selectedQuantity = newQty;
            this.applyBulkRate(targetItem);
            targetItem.cartError = undefined;

            if (candidate.platform === 'hyperpure' && targetItem.sku_id && this.hyperpureSupplier) {
                this.hyperpureSupplier.syncCartLocally(String(targetItem.sku_id));
                targetItem.isAddingToCart = true;

                this.priceDiscoveryService.addToCart(Number(targetItem.sku_id), newQty, 'DELIVERY', this.selectedEntityId)
                    .subscribe({
                        next: () => {
                            targetItem!.isAddingToCart = false;
                            onComplete();
                        },
                        error: () => {
                            targetItem!.isAddingToCart = false;
                            targetItem!.selectedQuantity = previousQty;
                            this.hyperpureSupplier.syncCartLocally(String(targetItem!.sku_id));
                            this.marketBestItemSkus.delete(String(targetItem!.sku_id));
                            errorCount++;
                            onComplete();
                        }
                    });
            } else if (candidate.platform === 'dmart' && targetItem.sku_id && this.dmartSupplier) {
                this.dmartSupplier.syncCartLocally(String(targetItem.sku_id));
                targetItem.isAddingToCart = true;

                this.priceDiscoveryService.addToDmartCart(String(targetItem.sku_id), newQty, this.selectedEntityId)
                    .subscribe({
                        next: () => {
                            targetItem!.isAddingToCart = false;
                            onComplete();
                        },
                        error: () => {
                            targetItem!.isAddingToCart = false;
                            targetItem!.selectedQuantity = previousQty;
                            this.dmartSupplier.syncCartLocally(String(targetItem!.sku_id));
                            this.marketBestItemSkus.delete(String(targetItem!.sku_id));
                            errorCount++;
                            onComplete();
                        }
                    });
            } else {
                onComplete();
            }
        });
    }

    removeAllMarketBestItems(platform?: string): void {
        const snapshotsToProcess = platform
            ? this.marketBestItemsSnapshot.filter(s => s.platform === platform)
            : [...this.marketBestItemsSnapshot];

        if (snapshotsToProcess.length === 0) {
            this.sharedService.warningAlert('No Market Best items to undo');
            return;
        }

        snapshotsToProcess.forEach(snapshot => {
            const section = this.supplierSections.find(s => s.id === snapshot.platform);
            if (!section) return;

            const targetItem = section.items.find(i => String(i.sku_id) === snapshot.skuId);
            if (targetItem) {
                targetItem.selectedQuantity = snapshot.previousQty;
                this.applyBulkRate(targetItem);
            }
            this.marketBestItemSkus.delete(snapshot.skuId);
        });

        this.marketBestItemsSnapshot = this.marketBestItemsSnapshot.filter(
            s => !snapshotsToProcess.some(p => p.skuId === s.skuId)
        );
        this.hasMarketBestItems = this.marketBestItemSkus.size > 0;
        this.saveMarketBestItems();
        this.refreshCheckoutCart();

        this.sharedService.successAlert(`${snapshotsToProcess.length} Market Best item(s) removed`);
    }

    get marketBestItems(): SupplierProduct[] {
        if (!this.selectedCheckoutCart) return [];
        return (this.selectedCheckoutCart.items || []).filter(
            (item: SupplierProduct) =>
                item.selectedQuantity > 0 &&
                this.marketBestItemSkus.has(String(item.sku_id))
        );
    }

    get sessionModifiedItemsExcludingMarketBest(): SupplierProduct[] {
        return this.sessionModifiedItems.filter(
            item => !this.marketBestItemSkus.has(String(item.sku_id))
        );
    }

    get nonMarketBestCartItems(): SupplierProduct[] {
        if (!this.selectedCheckoutCart) return [];
        return (this.selectedCheckoutCart.items || []).filter(
            (item: SupplierProduct) => !this.marketBestItemSkus.has(String(item.sku_id))
        );
    }

    private get marketBestStorageKey(): string {
        return `marketBestItems_direct_${this.selectedEntityId}`;
    }

    private saveMarketBestItems(): void {
        try {
            const data = {
                skus: [...this.marketBestItemSkus],
                snapshot: this.marketBestItemsSnapshot
            };
            localStorage.setItem(this.marketBestStorageKey, JSON.stringify(data));
        } catch (e) {
            console.error('Failed to save market best items:', e);
        }
    }

    private loadMarketBestItems(): void {
        try {
            const stored = localStorage.getItem(this.marketBestStorageKey);
            if (stored) {
                const data = JSON.parse(stored);
                this.marketBestItemSkus = new Set(data.skus || []);
                this.marketBestItemsSnapshot = data.snapshot || [];
                this.hasMarketBestItems = this.marketBestItemSkus.size > 0;
            }
        } catch (e) {
            this.marketBestItemSkus = new Set();
            this.marketBestItemsSnapshot = [];
            this.hasMarketBestItems = false;
        }
    }

    private clearMarketBestItems(): void {
        this.marketBestItemSkus = new Set();
        this.marketBestItemsSnapshot = [];
        this.hasMarketBestItems = false;
        this.isAddingBestMatches = false;
        this.isBestMatchPopupOpen = false;
        this.selectAllBestMatches = false;
        this.bestMatchCandidates = [];
        localStorage.removeItem(this.marketBestStorageKey);
    }

    get allSearchesComplete(): boolean {
        return this.searchItems.length > 0 && this.searchItems.every(item => !item.isLoading);
    }

    // ─────────────────────────────────────────────────────────────
    // Favourite Products - Same as original
    // ─────────────────────────────────────────────────────────────

    get filteredSidebarFavourites(): PmFavProduct[] {
        if (!this.favSidebarSearchTerm) return this.favProductsList;
        const term = this.favSidebarSearchTerm.toLowerCase();
        return this.favProductsList.filter((fav: PmFavProduct) => {
            const name = fav.productName || '';
            const supplier = fav.supplierDisplayName || fav.supplierName || '';
            return name.toLowerCase().includes(term) || supplier.toLowerCase().includes(term);
        });
    }

    loadAllFavItems(): void {
        if (!this.selectedEntityId || this.isLoadingAllFav) return;

        this.isFavViewMode = true;
        this.isLoadingAllFav = true;
        this.activeFavItem = '';

        this.supplierSections.forEach(section => {
            section.items = section.items.filter(item => item.searchItemName !== '__fav__');
            section.totalItems = section.items.length;
        });

        this.activeSearchItem = '__fav__';
        this.marketBestPrice = null;

        this.directPriceDiscoveryService.searchFavouriteItems(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.isLoadingAllFav = false;
                    const data = response?.response || response;

                    if (!data) {
                        this.sharedService.warningAlert('No results found for favourite items');
                        return;
                    }

                    const favSearchItem: SearchItem = { name: '__fav__', isLoading: false };
                    const productImages = data?.product_images || {};
                    this.processDirectResults(data, favSearchItem);
                },
                (error: any) => {
                    this.isLoadingAllFav = false;
                    console.error('Failed to search favourite items:', error);
                    this.sharedService.errorAlert('Failed to load favourite items.');
                }
            );
    }

    exitFavViewMode(): void {
        this.isFavViewMode = false;
        this.isLoadingAllFav = false;
        this.activeFavItem = '';

        this.supplierSections.forEach(section => {
            section.items = section.items.filter(item => item.searchItemName !== '__fav__');
            section.totalItems = section.items.length;
        });

        if (this.searchItems.length > 0) {
            this.activeSearchItem = this.searchItems[this.searchItems.length - 1].name;
            this.refreshMarketBestPriceForActiveSearch();
        } else {
            this.activeSearchItem = '';
            this.marketBestPrice = null;
        }
    }

    loadFavouriteProducts(): void {
        if (!this.selectedEntityId) return;

        this.isLoadingFavProducts = true;
        this.directPriceDiscoveryService.getFavouriteProducts(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.isLoadingFavProducts = false;
                    const rawData = response?.data || response || [];

                    if (Array.isArray(rawData)) {
                        this.favProductsList = rawData.map((item: any) => ({
                            _id: item._id || item.id,
                            companyId: item.companyId || item.company_id,
                            supplierName: item.supplierName || item.supplier_name,
                            supplierDisplayName: item.supplierDisplayName || item.supplier_display_name,
                            productName: item.productName || item.product_name,
                            productSkuId: item.productSkuId || item.product_sku_id,
                            supplierId: item.supplierId || item.supplier_id,
                            createdAt: item.createdAt || item.created_at,
                            updatedAt: item.updatedAt || item.updated_at,
                        } as PmFavProduct));
                        this.favProductKeys = new Set(
                            this.favProductsList.map((fav: PmFavProduct) =>
                                buildFavProductKey(fav.supplierName, fav.productSkuId)
                            )
                        );
                    } else {
                        this.favProductsList = [];
                        this.favProductKeys = new Set();
                    }
                },
                (error: any) => {
                    this.isLoadingFavProducts = false;
                    console.error('Failed to load favourite products:', error);
                }
            );
    }

    private resolveSupplierNameForFav(sectionId: string): string {
        const id = (sectionId || '').toLowerCase();
        if (id === 'hyperpure' || id === 'hp') return 'Hyperpure';
        if (id === 'udaan') return 'Udaan';
        if (id === 'dmart') return 'Dmart';
        return 'Local Supplier';
    }

    private resolveSupplierDisplayName(sectionId: string, section?: SupplierSection): string {
        const canonical = this.resolveSupplierNameForFav(sectionId);
        if (canonical !== 'Local Supplier') return '';
        if (section?.name) return section.name;
        return this.resolveSupplierName(sectionId) || sectionId;
    }

    isSupplierProductFav(sectionId: string, item: SupplierProduct): boolean {
        const supplierName = this.resolveSupplierNameForFav(sectionId);
        const productId = (supplierName === 'Hyperpure' && item?.product_number)
            ? String(item.product_number)
            : String(item?.sku_id || item?.item_name || '');
        if (!productId) return false;
        return this.favProductKeys.has(buildFavProductKey(supplierName, productId));
    }

    toggleProductFavourite(item: SupplierProduct, section: SupplierSection, event?: Event): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        if (!this.selectedEntityId) {
            this.sharedService.errorAlert('Please select an entity first');
            return;
        }

        const sectionId = section.id || '';
        const supplierName = this.resolveSupplierNameForFav(sectionId);
        const productSkuId = (supplierName === 'Hyperpure' && item.product_number)
            ? String(item.product_number)
            : String(item.sku_id || item.item_name || '');
        const productName = item.item_name || '';

        if (!productSkuId) return;

        const key = buildFavProductKey(supplierName, productSkuId);

        if (this.savingFavProduct.has(key)) return;

        const isFav = this.favProductKeys.has(key);

        if (isFav) {
            this.favProductKeys.delete(key);
            this.favProductsList = this.favProductsList.filter(
                (f) => buildFavProductKey(f.supplierName, f.productSkuId) !== key
            );
        } else {
            this.favProductKeys.add(key);
        }

        this.savingFavProduct.add(key);

        if (isFav) {
            const payload: RemoveFavProductPayload = {
                companyId: this.selectedEntityId,
                supplierName,
                productSkuId,
            };

            this.directPriceDiscoveryService.removeFavouriteProduct(payload)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    () => {
                        this.savingFavProduct.delete(key);
                    },
                    (error: any) => {
                        this.savingFavProduct.delete(key);
                        this.favProductKeys.add(key);
                        this.loadFavouriteProducts();
                        this.sharedService.errorAlert('Failed to remove from favourites');
                    }
                );
        } else {
            const payload: AddFavProductPayload = {
                companyId: this.selectedEntityId,
                supplierName,
                supplierDisplayName: this.resolveSupplierDisplayName(sectionId, section),
                productName,
                productSkuId,
                ...(supplierName === 'Local Supplier' && item.supplierId ? { supplierId: item.supplierId } : {}),
            };

            this.directPriceDiscoveryService.addFavouriteProduct(payload)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    (response: any) => {
                        this.savingFavProduct.delete(key);
                        const favProduct: PmFavProduct = {
                            _id: response?.data?._id || response?.data?.id,
                            companyId: payload.companyId,
                            supplierName: payload.supplierName as PmFavProduct['supplierName'],
                            supplierDisplayName: payload.supplierDisplayName,
                            productName: payload.productName,
                            productSkuId: payload.productSkuId,
                            supplierId: payload.supplierId,
                            createdAt: response?.data?.createdAt || response?.data?.created_at,
                            updatedAt: response?.data?.updatedAt || response?.data?.updated_at,
                        };
                        this.favProductsList.push(favProduct);
                    },
                    (error: any) => {
                        this.savingFavProduct.delete(key);
                        this.favProductKeys.delete(key);
                        if (error?.status === 409) {
                            this.loadFavouriteProducts();
                        } else {
                            this.sharedService.errorAlert('Failed to add to favourites');
                        }
                    }
                );
        }
    }
}
